// imported from myintelli-onnx commit 9f78984 on 2026-05-04
// do not edit manually — regenerate via `node scripts/sync-onnx-vendor.mjs`
/**
 * Single source of truth for pipeline parameters — TypeScript twin of
 * core/python/intelliid_core/config.py. When changing a default here, change
 * it there in the same commit.
 *
 * The numbers are duplicated rather than fetched at runtime because:
 *   • Mobile bundles can't import Python.
 *   • Tests in both languages need to see the same constants.
 *   • A drift between the two is caught by `tests/parity.test.ts`.
 */

export interface DetectorConfig {
  modelPath: string;
  inputSize: number;
  scoreThreshold: number;
  nmsThreshold: number;
  maxFaces: number;
}

export interface EmbedderConfig {
  modelPath: string;
  inputSize: number;
  embeddingDim: number;
  /** 5-point landmark template for similarity-transform alignment in 112x112 space */
  landmarkTemplate: ReadonlyArray<readonly [number, number]>;
}

export interface AntiSpoofConfig {
  modelPath: string;
  inputSize: number;
  passiveHigh: number;
  passiveLow: number;
  activeRequiredWhenBorderline: boolean;
}

export interface QualityConfig {
  blurMinLaplacianVar: number;
  brightnessMin: number;
  brightnessMax: number;
  faceMinShortSidePx: number;
  yawMaxDeg: number;
  pitchMaxDeg: number;
}

export interface RecognitionConfig {
  thresholdCosine: number;
  topK: number;
  useHybridScore: boolean;
}

export interface IndexConfig {
  space: 'cosine' | 'ip';
  efConstruction: number;
  M: number;
  efSearch: number;
  initialCapacity: number;
}

export interface LoggingConfig {
  verbose: boolean;
  frameRetentionDays: number;
  fieldTestMode: boolean;
}

export interface PipelineConfig {
  detector: DetectorConfig;
  embedder: EmbedderConfig;
  antispoof: AntiSpoofConfig;
  quality: QualityConfig;
  recognition: RecognitionConfig;
  index: IndexConfig;
  logging: LoggingConfig;

  applyClahe: boolean;
  claheClipLimit: number;
  claheTileGrid: readonly [number, number];
  applyGamma: boolean;
  gammaLowLumThreshold: number;
  gammaHighLumThreshold: number;
}

export const ARCFACE_LANDMARK_TEMPLATE: ReadonlyArray<readonly [number, number]> = [
  [38.2946, 51.6963],
  [73.5318, 51.5014],
  [56.0252, 71.7366],
  [41.5493, 92.3655],
  [70.7299, 92.2041],
];

export function defaultConfig(): PipelineConfig {
  return {
    detector: {
      modelPath: 'models/weights/scrfd_500m.onnx',
      inputSize: 640,
      scoreThreshold: 0.5,
      nmsThreshold: 0.4,
      maxFaces: 5,
    },
    embedder: {
      modelPath: 'models/weights/mobilefacenet.onnx',
      inputSize: 112,
      embeddingDim: 512,
      landmarkTemplate: ARCFACE_LANDMARK_TEMPLATE,
    },
    antispoof: {
      modelPath: 'models/weights/minifasnetv2.onnx',
      inputSize: 80,
      passiveHigh: 0.85,
      passiveLow: 0.4,
      activeRequiredWhenBorderline: true,
    },
    quality: {
      blurMinLaplacianVar: 100,
      brightnessMin: 40,
      brightnessMax: 220,
      faceMinShortSidePx: 112,
      yawMaxDeg: 25,
      pitchMaxDeg: 20,
    },
    recognition: {
      thresholdCosine: 0.6,
      topK: 5,
      useHybridScore: true,
    },
    index: {
      space: 'cosine',
      efConstruction: 200,
      M: 16,
      efSearch: 50,
      initialCapacity: 1000,
    },
    logging: {
      verbose: false,
      frameRetentionDays: 30,
      fieldTestMode: false,
    },
    applyClahe: true,
    claheClipLimit: 2.0,
    claheTileGrid: [8, 8],
    applyGamma: true,
    gammaLowLumThreshold: 60,
    gammaHighLumThreshold: 200,
  };
}
